import { useState, useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getTourSteps } from './tourSteps';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAppContext } from 'providers/appProvider';
import WelcomeModal from './WelcomeModal';
import { getCompletedTasks } from './useOnboardingProgress';

export const RESTART_TOUR_EVENT = 'onboarding:restart';
export const ONBOARDING_DISMISSED_EVENT = 'onboarding:dismissed';
export const OPEN_GET_STARTED_EVENT = 'onboarding:openGetStarted';

const OnboardingTour = () => {
  const { t } = useTranslation();
  const driverRef = useRef(null);
  const { config, setConfig } = useAppContext();
  const configRef = useRef(config);       
  const wasBurgerMenuOpenRef = useRef(false);
  const wasNavCollapsedRef = useRef(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    let stopPolling = null;

    const isMobileViewport = () => window.innerWidth < 1200;

    const openSidebarForTour = () => {
      const current = configRef.current;
      wasBurgerMenuOpenRef.current = current.showBurgerMenu;
      wasNavCollapsedRef.current = current.isNavbarVerticalCollapsed;

      if (isMobileViewport()) {
        if (!current.showBurgerMenu) {
          setConfig('showBurgerMenu', true);
        }
      }
      if (current.isNavbarVerticalCollapsed) {
        setConfig('isNavbarVerticalCollapsed', false);
      }

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    };

    const restoreSidebarAfterTour = () => {
      if (isMobileViewport()) {
        if (!wasBurgerMenuOpenRef.current) {
          setConfig('showBurgerMenu', false);
        }
      }
      if (wasNavCollapsedRef.current) {
        setConfig('isNavbarVerticalCollapsed', true);
      }
    };

    const pollAndStart = (isFirstTime = false) => {
      openSidebarForTour();

      const allSteps = getTourSteps(t).filter(step => {
        if (step.element === '[data-tour="tour-retake"]' && !isFirstTime) return false;
        if (step.element === '[data-tour="tour-location"]' && localStorage.getItem('UserRole') === 'Client') return false;
        return true;
      });
      let attempts = 0;
      const maxAttempts = isFirstTime ? 20 : 6;

      const pollForElements = setInterval(() => {
        attempts++;
        const availableSteps = allSteps.filter(step =>
          document.querySelector(step.element)
        );

        const allFound = availableSteps.length === allSteps.length;

        if (allFound || attempts >= maxAttempts) {
          clearInterval(pollForElements);
          stopPolling = null;
          if (availableSteps.length > 0) {
            setTimeout(() => startDriverTour(availableSteps, restoreSidebarAfterTour), 380);
          } else {
            console.warn('Onboarding tour: no target elements found after retries');
          }
        }
      }, 100);

      stopPolling = () => {
        clearInterval(pollForElements);
        stopPolling = null;
      };
    };

    const initTour = async () => {
      const userId = localStorage.getItem('UserId');
      const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

      if (!userId || !isLoggedIn) return;

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/getOnboardingStatus/${userId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        if (data.hasWatchedTutorial) {
          localStorage.setItem('hasWatchedTutorial', 'true');
        }

        if (Array.isArray(data.completedTasks) && data.completedTasks.length > 0) {
          const current = getCompletedTasks();
          const merged = Array.from(new Set([...current, ...data.completedTasks]));
          localStorage.setItem('onboarding_completed_tasks', JSON.stringify(merged));
          window.dispatchEvent(new Event('onboarding:taskUpdated'));
        }

      if (data.hasCompletedTourGuide) {
        localStorage.setItem('hasCompletedTourGuide', 'true');
        localStorage.setItem('hasCompletedGuidedTour', 'true');
        return;
      }

        localStorage.setItem('hasCompletedTourGuide', 'false');
        if (!data.hasWatchedTutorial && localStorage.getItem('hasWatchedTutorial') !== 'true') {
          setShowWelcomeModal(true);
        }
      } catch (err) {
        console.error('Failed to initialize onboarding status', err);
      }
    };

    const handleRestartEvent = () => {
      if (stopPolling) {
        stopPolling();
      }
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
      pollAndStart(false);
    };

        const handleOpenWelcome = () => {
      setShowWelcomeModal(true);
    };

    initTour();
    window.addEventListener(RESTART_TOUR_EVENT, handleRestartEvent);
    window.addEventListener('onboarding:openWelcome', handleOpenWelcome);

    return () => {
      window.removeEventListener(RESTART_TOUR_EVENT, handleRestartEvent);
      window.removeEventListener('onboarding:openWelcome', handleOpenWelcome);
      if (stopPolling) {
        stopPolling();
      }
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  const markComplete = async () => {
    const userId = localStorage.getItem('UserId');
    localStorage.setItem('hasCompletedTourGuide', 'true');
    window.dispatchEvent(new Event(ONBOARDING_DISMISSED_EVENT));
    if (!userId) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateTourGuideStatus/${userId}`,
        {},
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
    } catch (err) {
      console.error('Failed to persist tour guide status', err);
    }
  };

  const startDriverTour = (steps, onTourEnd) => {
    let reachedLastStep = false;
    const driverObj = driver({
      showProgress: true,
      allowClose: false,
      showButtons: ['next', 'previous', 'close'],
      overlayColor: 'rgba(0, 27, 61, 0.55)',
      stagePadding: 6,
      stageRadius: 6,
      popoverOffset: 24,
      popoverClass: 'onboarding-popover',
      smoothScroll: true,
      animate: true,
      nextBtnText: t('onboardingTour.next'),
      prevBtnText: t('onboardingTour.back'),
      doneBtnText: t('onboardingTour.done'),
      progressText: t('onboardingTour.progressText', { current: '{{current}}', total: '{{total}}' }),
      steps,
      onPopoverRender: (popover, { config: stepConfig, state }) => {
        const currentStep = state.activeStep;

        if (currentStep?.popover?.imageUrl) {
          const img = document.createElement('img');
          img.src = currentStep.popover.imageUrl;
          img.alt = currentStep.popover.title || '';
          img.className = 'onboarding-tour-image';
          popover.description.append(img);
        }

        if (popover.progress && popover.title) {
          const headerRow = document.createElement('div');
          headerRow.className = 'onboarding-header-row';

          popover.title.parentNode.insertBefore(headerRow, popover.title);
          headerRow.appendChild(popover.title);
          headerRow.appendChild(popover.progress);
        }
      },
      onHighlightStarted: (element) => {
        if (driverObj.getActiveIndex && driverObj.getActiveIndex() >= steps.length - 1) {
          reachedLastStep = true;
        }

        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

          if (!element.classList.contains('active')) {
            element.classList.add('active');
            element.dataset.tempActive = 'true';
          }
        }
      },
      onDeselected: (element) => {
        if (element && element.dataset.tempActive === 'true') {
          element.classList.remove('active');
          delete element.dataset.tempActive;
        }
      },
      onCloseClick: () => {          
        driverRef.current?.destroy();
      },
      onDestroyed: () => {
        if (reachedLastStep || (driverObj.getActiveIndex && driverObj.getActiveIndex() >= steps.length - 1)) {
          localStorage.setItem('hasCompletedGuidedTour', 'true');
          window.dispatchEvent(new Event('onboarding:tourCompleted'));
        }
        markComplete();
        onTourEnd?.();
        driverRef.current = null;
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
  };

  const handleCloseWelcome = () => {
    setShowWelcomeModal(false);
    markComplete();
  };

  return (
    <WelcomeModal
      show={showWelcomeModal}
      onHide={handleCloseWelcome}
    />
  );
};

export default OnboardingTour;